import { Component } from './Component';
import { SecurityError } from '../errors/SecurityError';
import { Validator } from '../validators/Validator';
import { ApiService } from '../services/ApiService';
import { SessionService } from '../services/SessionService';
import { InputEvent } from '../utils/InputEvent';

/**
 * Security Component.
 */
export class Security extends Component {
  /**
   * String of the dom element of the phoneNumber id.
   *
   * @type {string}
   */
  static phoneNumberDOMSelector = '#phoneNumber';

  /**
   * String of the dom element of the phoneNumber label id.
   *
   * @type {string}
   */
  static phoneNumberLabelDOMSelector = '#phoneNumberLabel';

  /**
   * String of the dom element of the submit button.
   *
   * @type {string}
   */
  static btnSubmitDOMSelector = '#btnSubmit';

  /**
   * String of the dom element of the main form.
   *
   * @type {string}
   */
  static mainFormDOMSelector = '#eui-form-security';

  /**
   * Constructor.
   *
   * @param   {string}  container - Container.
   */
  constructor(container) {
    super(container);
    this.SecurityError = new SecurityError();
  }

  /**
   * Render template.
   *
   * @param {object} opts - The parameters to render the component.
   */
  render(opts) {
    Component.updateTracking({ type_page: 'moyen_contact' });
    if (opts) {
      super.render(opts);
      this.attachListener();
      // Tell the visually impaired that the page has changed
      super.announceNextPageLoad();
    }
  }

  /**
   * Attach listener for the actual component.
   *
   */
  attachListener() {
    this.phoneNumberDOM = document.querySelector(Security.phoneNumberDOMSelector);
    this.formDOM = document.querySelector(Security.mainFormDOMSelector);

    // Identity error
    this.securityError = new SecurityError();

    // Boolean State for all input
    this.isPhoneNumberValid = true;

    this.initValidatorValues();
    this.attachPhoneNumberEvent();
    this.attachSubmitEvent();
  }

  /**
   * Get phoneNumber value.
   *
   * @returns {string} PhoneNumber.
   */
  getPhoneNumberValue() {
    return this.phoneNumberDOM.value.trim();
  }

  /**
   * Init Validator Values.
   */
  initValidatorValues() {
    Validator.setPhoneNumberValidator(this.phoneNumberDOM.getAttribute('pattern'));
  }

  /**
   * Attach phoneNumber event.
   */
  attachPhoneNumberEvent() {
    if (this.phoneNumberDOM) {
      this.phoneNumberDOM.addEventListener('blur', () => {
        InputEvent.handleOb1Behaviour(this.phoneNumberDOM);
        this.controlPhoneNumberSyntax();
      });
    }
  }

  /**
   * Attach Submit event.
   */
  attachSubmitEvent() {
    if (this.formDOM) {
      this.formDOM.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitSecurityData();
      });
    }
  }

  /**
   * Control the syntax of the phone number.
   *
   */
  controlPhoneNumberSyntax() {
    if (this.phoneNumberDOM) {
      const value = this.getPhoneNumberValue();
      this.isPhoneNumberValid = Validator.isPhoneNumberValid(value);
      this.securityError.handleErrorPhoneNumber(this.isPhoneNumberValid, value);
    }
  }

  /**
   * Is form valid for the submit button.
   */
  isFormValid() {
    return this.isPhoneNumberValid;
  }

  /**
   * Submit Security Data.
   */
  submitSecurityData() {
    // Check all value before submit and upgrade state of boolean value
    this.controlPhoneNumberSyntax();

    if (this.isFormValid()) {
      const opt = {
        url: ApiService.apiSchema.security.url,
        method: ApiService.apiSchema.security.method,
        data: {
          phoneNumber: this.getPhoneNumberValue(),
        },
      };

      return Component.makeRequest(opt)
        .then((requestPending) => {
          if (requestPending !== true) {
            SessionService.resetSessionTimer();
          }
        })
        .catch(() => undefined);
    }
    if (this.phoneNumberDOM) {
      this.phoneNumberDOM.focus();
    }
  }

  /**
   * Back event treatment for gdpr components.
   *
   * @returns {Promise} Promise of the component make Requests.
   */
  back() {
    const opt = {
      url: ApiService.apiSchema.security.back.url,
      method: ApiService.apiSchema.security.back.method,
    };

    return Component.makeRequest(opt, true)
      .then(() => SessionService.restartSessionTimer());
  }
}
